import React from "react";
import * as Icon from "react-feather";
import clsx from "clsx";
import BookkeepingService from "../../../../services/BookkeepingService";
import ToolsSelect from "../kit/ToolsSelect";
import UtilityService from "../../../../services/UtilityService";
import ColSelect from "../../../common/col_select/ColSelect";
import WComponent from "../../../common/WComponent";
import "./opening-balance-entry.css";

const round2 = n => Math.round((parseFloat(n) || 0) * 100) / 100;
const blankRow = () => ({ key: Math.random().toString(36).slice(2), accountNumber: "", accountName: "", debit: "", credit: "", canonical_code: null, display_name: "" });
const isLiveRow = row => (row.accountName || row.accountNumber) && (round2(row.debit) !== 0 || round2(row.credit) !== 0);

class OpeningBalanceEntry extends WComponent {
	constructor(props) {
		super(props);
		this.state = {
			rows: [blankRow(), blankRow(), blankRow()],
			taxonomy: [],
			saving: false,
			earningsClose: null,
			earningsCloseAccountId: null,
			designating: false,
			suggesting: false,
			error: null,
			warning: null,
			editingAmountCell: null,
			searchText: "",
			statusFilter: "all",
			sortField: null,
			sortOrder: "asc",

			importing: false,
			importData: null,
			colMap: { number: -1, name: -1, debit: -1, credit: -1, balance: -1 },
			useBalance: false,
			importError: null,
		};
	}

	componentDidMount = async () => {
		const taxonomy = await BookkeepingService.getCanonicalTaxonomy();
		await this.update({ taxonomy: Array.isArray(taxonomy) ? taxonomy : [] });
		await this.loadExistingOpeningBalances();

		const hasPrefill = Array.isArray(this.props.prefillLines) && this.props.prefillLines.length > 0;
		if (hasPrefill && this.getLiveRows().length === 0) {
			await this.applyPrefillLines(this.props.prefillLines);
		}
	};

	applyPrefillLines = async lines => {
		const rows = lines.map(line => ({
			key: Math.random().toString(36).slice(2),
			accountNumber: line.accountNumber || "",
			accountName: line.accountName || line.accountDescription || "",
			debit: line.debit ? String(line.debit) : "",
			credit: line.credit ? String(line.credit) : "",
			canonical_code: line.canonical_code || null,
			display_name: line.display_name || "",
		}));
		await this.update({ rows });
		const hasUnmappedRow = rows.some(row => isLiveRow(row) && !row.canonical_code);
		if (hasUnmappedRow) await this.onAutoMap();
	};

	componentDidUpdate(prevProps) {
		if (prevProps.wpfEngagementId !== this.props.wpfEngagementId) {
			this.loadExistingOpeningBalances();
		}
	}

	loadExistingOpeningBalances = async () => {
		const { wpfEngagementId } = this.props;
		if (!wpfEngagementId) return;
		const result = await BookkeepingService.getOpeningBalances({ wpfEngagementId });
		if (!result || !Array.isArray(result.lines) || result.lines.length === 0) return;
		await this.update({
			rows: result.lines.map(line => ({
				key: Math.random().toString(36).slice(2),
				accountNumber: line.accountNumber || "",
				accountName: line.accountDescription || line.display_name || "",
				debit: line.debit ? line.debit : "",
				credit: line.credit ? line.credit : "",
				canonical_code: line.canonical_code || null,
				display_name: line.display_name || "",
			})),
		});
	};

	getLiveRows = () => this.state.rows.filter(isLiveRow);

	rowMatchesSearch = row => {
		const searchTerm = this.state.searchText.trim().toLowerCase();
		if (!searchTerm) return true;
		const searchableText = `${row.accountNumber} ${row.accountName} ${row.display_name}`.toLowerCase();
		return searchableText.includes(searchTerm);
	};

	rowMatchesStatusFilter = row => {
		const { statusFilter } = this.state;
		if (statusFilter === "unmapped") return isLiveRow(row) && !row.canonical_code;
		if (statusFilter === "debits") return round2(row.debit) !== 0;
		if (statusFilter === "credits") return round2(row.credit) !== 0;
		return true;
	};

	compareRowsForSort = (rowA, rowB) => {
		const { sortField, sortOrder } = this.state;
		const direction = sortOrder === "desc" ? -1 : 1;

		if (sortField === "debit" || sortField === "credit") {
			return (round2(rowA[sortField]) - round2(rowB[sortField])) * direction;
		}

		const textOf = row => (sortField === "fs" ? row.display_name || "" : row[sortField] || "");
		return textOf(rowA).localeCompare(textOf(rowB), undefined, { numeric: true, sensitivity: "base" }) * direction;
	};

	getVisibleRows = () => {
		const matchingRows = this.state.rows.filter(row => this.rowMatchesSearch(row) && this.rowMatchesStatusFilter(row));
		if (!this.state.sortField) return matchingRows;

		const liveRowsSorted = matchingRows.filter(isLiveRow).sort(this.compareRowsForSort);
		const blankRows = matchingRows.filter(row => !isLiveRow(row));
		return [...liveRowsSorted, ...blankRows];
	};

	isFilterActive = () => this.state.searchText.trim() !== "" || this.state.statusFilter !== "all";

	onSortColumn = field => {
		const isSameField = this.state.sortField === field;
		const sortOrder = isSameField && this.state.sortOrder === "asc" ? "desc" : "asc";
		this.update({ sortField: field, sortOrder });
	};

	getTaxonomyOptions = () =>
		this.state.taxonomy.map(node => ({ value: node.canonical_code, label: node.display_name }));

	setRowCanonicalCode = (key, canonicalCode) => {
		const node = this.state.taxonomy.find(taxonomyNode => taxonomyNode.canonical_code === canonicalCode);
		const displayName = node ? node.display_name : "";
		this.setState(prev => ({ rows: prev.rows.map(row => (row.key === key ? { ...row, canonical_code: canonicalCode, display_name: displayName } : row)) }));
	};

	updateRow = (key, field, value) => {
		this.setState(prev => ({ rows: prev.rows.map(row => (row.key === key ? { ...row, [field]: value } : row)) }));
	};

	removeRow = key => {
		this.setState(prev => ({ rows: prev.rows.length > 1 ? prev.rows.filter(row => row.key !== key) : prev.rows }));
	};

	addRow = () => {
		this.setState(prev => ({ rows: [...prev.rows, blankRow()], searchText: "", statusFilter: "all" }));
	};

	onAutoMap = async () => {
		const { clientId } = this.props;
		const liveRows = this.getLiveRows();
		if (!liveRows.length) return;

		await this.update({ suggesting: true });
		const accounts = liveRows.map(row => ({ accountNumber: row.accountNumber || row.accountName, accountName: row.accountName || row.accountNumber, gifiCode: null }));
		const result = await BookkeepingService.mapCoaSuggest({ clientId, accounts });
		await this.update({ suggesting: false });
		if (!result || !Array.isArray(result.suggestions)) return;

		const suggestionsByAccount = {};
		result.suggestions.forEach(suggestion => { suggestionsByAccount[suggestion.accountNumber || suggestion.accountName || suggestion.description] = suggestion; });

		const unmappedAccountKeys = new Set(this.getLiveRows().filter(row => !row.canonical_code).map(row => row.accountNumber || row.accountName));
		const appliedOnWordingAlone = result.suggestions.filter(suggestion =>
			suggestion.suggested_canonical_code
			&& suggestion.match_method === "unreviewed_guess"
			&& unmappedAccountKeys.has(suggestion.accountNumber || suggestion.accountName));

		this.setState(prev => ({
			rows: prev.rows.map(row => {
				if (row.canonical_code) return row;
				const suggestion = suggestionsByAccount[row.accountNumber || row.accountName];
				if (suggestion && suggestion.suggested_canonical_code) {
					return { ...row, canonical_code: suggestion.suggested_canonical_code, display_name: suggestion.suggested_display_name };
				}
				return row;
			}),
		}));
		if (Array.isArray(result.taxonomy) && result.taxonomy.length) await this.update({ taxonomy: result.taxonomy });

		if (appliedOnWordingAlone.length > 0) {
			const howMany = appliedOnWordingAlone.length;
			const reason = result.reviewUnavailableBecause ? ` — ${result.reviewUnavailableBecause}` : "";
			await this.update({ warning: `${howMany} account${howMany === 1 ? " was" : "s were"} matched on wording alone, without the AI check${reason}. Please check ${howMany === 1 ? "it" : "them"} before saving.` });
		}
	};

	onDesignateEarningsCloseAccount = async () => {
		const { wpfEngagementId, onComplete } = this.props;
		const { earningsCloseAccountId } = this.state;
		if (!earningsCloseAccountId) return;

		await this.update({ designating: true, error: null });
		const result = await BookkeepingService.designateEarningsCloseAccount({ wpfEngagementId, accountId: earningsCloseAccountId });
		await this.update({ designating: false });

		if (!result || !result.ok) {
			await this.update({ error: (result && result.reason) || "That account could not be recorded." });
			return;
		}

		await this.update({ earningsClose: null });
		if (onComplete) onComplete({ saved: true });
	};

	renderEarningsClose = () => {
		const { earningsClose, earningsCloseAccountId, designating } = this.state;
		const profit = earningsClose.netIncome;
		const isAProfit = profit >= 0;
		const options = earningsClose.equityAccounts.map(account => ({
			value: account.id,
			label: (account.accountNumber ? account.accountNumber + " " : "") + account.accountDescription,
		}));
		const chosenAccount = options.find(option => option.value === earningsCloseAccountId) || null;

		return (
			<div className="ob-entry__close">
				<h3 className="ob-entry__close-title">Where does last year's {isAProfit ? "profit" : "loss"} close to?</h3>
				<p className="ob-entry__close-body">
					The trial balance you imported is unclosed — its revenue and expenses still hold the year’s
					activity. Closing it into equity is what lets this year open from it.
				</p>
				<div className="ob-entry__close-entry">
					<div className="ob-entry__close-line">
						<span>Revenue less expenses</span>
						<strong>{UtilityService.formatCurrency(Math.abs(profit))}</strong>
					</div>
					<div className="ob-entry__close-line">
						<span>{isAProfit ? "Credited to" : "Debited to"}</span>
						<strong className={clsx(!chosenAccount && "ob-entry__close-pending")}>{chosenAccount ? chosenAccount.label : "choose below"}</strong>
					</div>
				</div>
				<label className="tk-form-label" htmlFor="earnings-close-account">Equity account<span className="tk-form-required">*</span></label>
				<ToolsSelect
					value={earningsCloseAccountId}
					options={options}
					onChange={value => this.update({ earningsCloseAccountId: value })}
					placeholder="Choose the account it closes into…"
				/>
				<p className="tk-form-help">Suggested from this engagement’s entity type. Change it if the client closes elsewhere.</p>
				<div className="ob-entry__close-actions">
					<button
						type="button"
						className="tk-button tk-button--primary"
						onClick={this.onDesignateEarningsCloseAccount}
						disabled={!earningsCloseAccountId || designating}
					>
						{designating ? "Recording…" : "Close the year into this account"}
					</button>
				</div>
			</div>
		);
	};

	onSave = async () => {
		const { clientId, wpfEngagementId, source = "manual", onComplete } = this.props;
		const liveRows = this.getLiveRows();

		await this.update({ saving: true, error: null });

		const chart = await BookkeepingService.getClientCoA({ clientId });
		const clientChart = chart && chart.accounts ? chart.accounts : [];
		const mappings = liveRows.map(row => {
			const label = BookkeepingService.describeChartAccount({ account: { accountNumber: row.accountNumber, accountName: row.accountName } });
			const chartRow = BookkeepingService.findChartRowForAccountName({ clientChart, accountName: label });
			return {
				accountId: chartRow && chartRow.id ? chartRow.id : null,
				accountNumber: row.accountNumber || null,
				accountName: row.accountName || null,
				canonical_code: row.canonical_code,
				source,
			};
		});
		const lines = liveRows.map(row => ({
			canonical_code: row.canonical_code,
			accountNumber: row.accountNumber || null,
			accountDescription: row.accountName || row.accountNumber || null,
			debit: round2(row.debit),
			credit: round2(row.credit),
		}));

		const chartSave = await BookkeepingService.mapCoaSave({ clientId, mappings });
		const chartCouldNotBeSavedBecause = chartSave && chartSave.error ? chartSave.error : null;

		const result = await BookkeepingService.saveOpeningBalances({ wpfEngagementId, source, lines });
		await this.update({ saving: false });

		if (!result || typeof result.saved !== "number") {
			await this.update({ error: (result && result.message) || "Failed to save opening balances." });
			return;
		}

		if (chartCouldNotBeSavedBecause) {
			await this.update({ warning: `The opening balances were saved, but these FS accounts were not kept on the client's chart, so they will have to be mapped again next time — ${chartCouldNotBeSavedBecause}` });
		}

		const closeNeedsADecision = result.earningsClose && result.earningsClose.reason;
		if (closeNeedsADecision) {
			await this.update({ earningsClose: result.earningsClose, earningsCloseAccountId: result.earningsClose.suggestedAccountId });
			return;
		}

		if (onComplete) onComplete(result);
	};

	onCsvFileSelected = async file => {
		await this.update({ importError: null });
		if (!file) return;

		let text;
		try { text = await file.text(); } catch (readError) { await this.update({ importError: "Could not read the file." }); return; }

		const matrix = UtilityService.parseCsv(text);
		if (matrix.length < 2) { await this.update({ importError: "That file has no data rows." }); return; }

		const headers = matrix[0];
		const dataRows = matrix.slice(1);
		const guessedColumnMap = {
			number: UtilityService.guessColumnIndex(headers, ["account number", "acct", "gl no", "gl #", "number", "code"]),
			name: UtilityService.guessColumnIndex(headers, ["name", "description", "account"]),
			debit: UtilityService.guessColumnIndex(headers, ["debit", "dr"]),
			credit: UtilityService.guessColumnIndex(headers, ["credit", "cr"]),
			balance: UtilityService.guessColumnIndex(headers, ["balance", "amount", "net"]),
		};

		await this.update({
			importData: { headers, dataRows },
			colMap: guessedColumnMap,
			useBalance: guessedColumnMap.debit === -1 && guessedColumnMap.credit === -1 && guessedColumnMap.balance !== -1,
			importing: true,
		});
	};

	onCsvFileInputChange = e => {
		this.onCsvFileSelected(e.target.files && e.target.files[0]);
	};

	onColumnMapChange = (field, columnIndex) => {
		this.setState(prev => ({ colMap: { ...prev.colMap, [field]: columnIndex } }));
	};

	onUseBalanceToggle = e => {
		this.update({ useBalance: e.target.checked });
	};

	onCancelImport = () => {
		this.update({ importing: false, importData: null, importError: null });
	};

	loadImportIntoGrid = () => {
		const { importData, colMap, useBalance } = this.state;
		if (!importData) return;

		const built = importData.dataRows
			.map(cells => {
				const accountNumber = colMap.number >= 0 ? (cells[colMap.number] || "") : "";
				const accountName = colMap.name >= 0 ? (cells[colMap.name] || "") : "";
				let debit = "";
				let credit = "";
				if (useBalance) {
					const balance = colMap.balance >= 0 ? UtilityService.parseAmount(cells[colMap.balance]) : 0;
					if (balance >= 0) debit = balance || ""; else credit = -balance;
				} else {
					const debitAmount = colMap.debit >= 0 ? UtilityService.parseAmount(cells[colMap.debit]) : 0;
					const creditAmount = colMap.credit >= 0 ? UtilityService.parseAmount(cells[colMap.credit]) : 0;
					debit = debitAmount || "";
					credit = creditAmount || "";
				}
				return { ...blankRow(), accountNumber, accountName, debit, credit };
			})
			.filter(isLiveRow);

		this.update({ rows: built.length ? built : [blankRow()], importing: false, importData: null });
	};

	renderColumnMappingScreen = () => {
		const { importData, colMap, useBalance, importError } = this.state;

		return (
			<div className="ob-entry">
				<h4 className="ob-entry__import-title">Map CSV columns</h4>
				{importError && <div className="ob-entry__error-text">{importError}</div>}
				<div className="ob-entry__colmap-grid">
					<ColSelect label="Account #" value={colMap.number} onChange={v => this.onColumnMapChange("number", v)} headers={importData.headers} />
					<ColSelect label="Account name" value={colMap.name} onChange={v => this.onColumnMapChange("name", v)} headers={importData.headers} />
					{useBalance ? (
						<ColSelect label="Balance (+ debit / − credit)" value={colMap.balance} onChange={v => this.onColumnMapChange("balance", v)} headers={importData.headers} />
					) : (
						<>
							<ColSelect label="Debit" value={colMap.debit} onChange={v => this.onColumnMapChange("debit", v)} headers={importData.headers} />
							<ColSelect label="Credit" value={colMap.credit} onChange={v => this.onColumnMapChange("credit", v)} headers={importData.headers} />
						</>
					)}
				</div>
				<label className="ob-entry__balance-toggle">
					<input type="checkbox" checked={useBalance} onChange={this.onUseBalanceToggle} />
					Single signed balance column (instead of separate debit/credit)
				</label>
				<div className="ob-entry__row-count">{importData.dataRows.length} row(s) detected.</div>
				<div className="ob-entry__import-footer">
					<button type="button" className="tk-button tk-button--secondary" onClick={this.onCancelImport}>Cancel</button>
					<button type="button" className="tk-button tk-button--primary" onClick={this.loadImportIntoGrid} disabled={colMap.name < 0 && colMap.number < 0}>Load into grid</button>
				</div>
			</div>
		);
	};

	renderSortIcon = field => {
		const { sortField, sortOrder } = this.state;
		const activeAsc = sortField === field && sortOrder === "asc";
		const activeDesc = sortField === field && sortOrder === "desc";
		return (
			<span className="ob-entry__sort-icon">
				<Icon.ChevronUp size={11} className={clsx("ob-entry__sort-arrow", activeAsc && "ob-entry__sort-arrow--active")} />
				<Icon.ChevronDown size={11} className={clsx("ob-entry__sort-arrow", activeDesc && "ob-entry__sort-arrow--active")} />
			</span>
		);
	};

	renderSortableHeader = ({ field, label, className }) => (
		<div className={clsx("t__header__item", className, "ob-entry__sortable-header")} onClick={() => this.onSortColumn(field)}>
			{label}
			{this.renderSortIcon(field)}
		</div>
	);

	renderFilterChip = ({ value, label }) => (
		<button
			key={value}
			type="button"
			className={clsx("ob-entry__filter-chip", this.state.statusFilter === value && "ob-entry__filter-chip--active")}
			onClick={() => this.update({ statusFilter: value })}
		>
			{label}
		</button>
	);

	renderFilterBar = () => {
		const { searchText } = this.state;
		const { unmappedCount } = this.getRowCounts();

		const chips = [
			{ value: "all", label: "All" },
			{ value: "unmapped", label: unmappedCount > 0 ? `Unmapped (${unmappedCount})` : "Unmapped" },
			{ value: "debits", label: "Debits" },
			{ value: "credits", label: "Credits" },
		];

		return (
			<div className="ob-entry__filters">
				<div className="tk-search ob-entry__search">
					<Icon.Search size={14} className="tk-search__icon" />
					<input
						className="tk-search__input"
						placeholder="Search accounts…"
						value={searchText}
						onChange={e => this.update({ searchText: e.target.value })}
					/>
				</div>
				<div className="ob-entry__filter-chips">{chips.map(this.renderFilterChip)}</div>
			</div>
		);
	};

	getRowCounts = () => {
		const liveRows = this.getLiveRows();
		const unmappedCount = liveRows.filter(row => !row.canonical_code).length;
		return { liveCount: liveRows.length, unmappedCount };
	};

	renderAmountInput = (row, field) => {
		const cellId = `${row.key}:${field}`;
		const isEditingThisCell = this.state.editingAmountCell === cellId;
		const rawAmount = row[field];
		const displayAmount = isEditingThisCell || rawAmount === "" ? rawAmount : UtilityService.formatCurrency(rawAmount);
		return (
			<input
				type="text"
				inputMode="decimal"
				value={displayAmount}
				onFocus={() => this.update({ editingAmountCell: cellId })}
				onBlur={() => this.update({ editingAmountCell: null })}
				onChange={e => this.updateRow(row.key, field, e.target.value.split(",").join(""))}
			/>
		);
	};

	renderRow = row => {
		const isUnmapped = isLiveRow(row) && !row.canonical_code;

		return (
			<div key={row.key} className={clsx("t__row t__row--lined ob-row", isUnmapped && "ob-row--unmapped")}>
				<div className="t__row__item ob-col-num">
					<input value={row.accountNumber} onChange={e => this.updateRow(row.key, "accountNumber", e.target.value)} placeholder={this.props.source === "manual" ? "1000" : ""} />
				</div>
				<div className="t__row__item ob-col-name">
					<input value={row.accountName} onChange={e => this.updateRow(row.key, "accountName", e.target.value)} placeholder="Cash in bank" />
				</div>
				<div className="t__row__item ob-col-fs">
					<ToolsSelect
						value={row.canonical_code || ""}
						options={this.getTaxonomyOptions()}
						onChange={canonicalCode => this.setRowCanonicalCode(row.key, canonicalCode)}
						placeholder="Search FS account…"
						searchable
						searchPlaceholder="Search FS accounts…"
						inline
					/>
				</div>
				<div className="t__row__item ob-col-amt">
					{this.renderAmountInput(row, "debit")}
				</div>
				<div className="t__row__item ob-col-amt">
					{this.renderAmountInput(row, "credit")}
				</div>
				<div className="t__row__item ob-col-x">
					<button className="ob-remove-btn" title="Remove row" onClick={() => this.removeRow(row.key)}><Icon.X size={14} /></button>
				</div>
			</div>
		);
	};

	render() {
		const { wpfEngagementId, onClose, hideTitle } = this.props;
		const { rows, saving, suggesting, error, warning, importing, importData, importError, earningsClose } = this.state;

		if (importing && importData) return this.renderColumnMappingScreen();
		if (earningsClose) return this.renderEarningsClose();

		const liveRows = this.getLiveRows();
		const visibleRows = this.getVisibleRows();
		const filterIsActive = this.isFilterActive();
		const tieOut = BookkeepingService.summarizeOpeningBalanceTieOut({ openingBalances: rows });
		const totalDebit = tieOut.debitTotal;
		const totalCredit = tieOut.creditTotal;
		const imbalance = tieOut.difference;
		const isBalanced = tieOut.isBalanced;
		const unmappedCount = liveRows.filter(row => !row.canonical_code).length;
		const canSave = liveRows.length > 0 && isBalanced && unmappedCount === 0 && !!wpfEngagementId;

		return (
			<div className="ob-entry">
				<div className="ob-entry__header">
					{!hideTitle && <h3 className="ob-entry__title">Enter Prior-Year Trial Balance</h3>}
					<p className="ob-entry__subtitle">
						Enter each account's prior-year balance and the FS account it maps to. The trial balance
						must balance (total debits = total credits) and every account must be mapped before saving.
					</p>
				</div>

				<div className="ob-entry__toolbar">
					<label className="ob-entry__link">
						<Icon.Upload size={14} /> Import from CSV
						<input type="file" accept=".csv,text/csv" className="ob-entry__file-input-hidden" onChange={this.onCsvFileInputChange} />
					</label>
					<button className="ob-entry__link" onClick={this.onAutoMap} disabled={suggesting || liveRows.length === 0}>
						<Icon.Zap size={14} /> {suggesting ? "Matching…" : "Auto-map FS accounts"}
					</button>
					{importError && <span className="ob-entry__error-text">{importError}</span>}
					<div className="ob-entry__toolbar-status">
						{isBalanced ? (
							<span className="ob-entry__status ob-entry__status--balanced"><Icon.CheckCircle size={14} /> In balance</span>
						) : (
							<span className="ob-entry__status ob-entry__status--error"><Icon.AlertCircle size={14} /> Out of balance by {UtilityService.formatCurrency(Math.abs(imbalance))}</span>
						)}
						{unmappedCount > 0 && (
							<span className="ob-entry__status ob-entry__status--warning"><Icon.AlertCircle size={14} /> {unmappedCount} unmapped</span>
						)}
					</div>
				</div>

				{this.renderFilterBar()}

				{error && <div className="ob-entry__banner ob-entry__banner--error">{error}</div>}
				{warning && <div className="ob-entry__banner ob-entry__banner--warning">{warning}</div>}
				{!wpfEngagementId && (
					<div className="ob-entry__banner ob-entry__banner--warning">
						{this.props.contextError
							? `Couldn't prepare the engagement, so opening balances can't be saved — ${this.props.contextError}`
							: "No engagement selected — opening balances can't be saved."}
					</div>
				)}

				<div className="t t--compact t--bordered ob-entry__table">
					<div className="t__header">
						{this.renderSortableHeader({ field: "accountNumber", label: "Account #", className: "ob-col-num" })}
						{this.renderSortableHeader({ field: "accountName", label: "Account name", className: "ob-col-name" })}
						{this.renderSortableHeader({ field: "fs", label: "FS account", className: "ob-col-fs" })}
						{this.renderSortableHeader({ field: "debit", label: "Debit", className: "ob-col-amt" })}
						{this.renderSortableHeader({ field: "credit", label: "Credit", className: "ob-col-amt" })}
						<div className="t__header__item ob-col-x" />
					</div>

					{visibleRows.map(this.renderRow)}

					{visibleRows.length === 0 && (
						<div className="ob-entry__no-match">No accounts match — clear the search or filter to see all {rows.length} rows.</div>
					)}

					{filterIsActive && visibleRows.length > 0 && (
						<div className="ob-entry__filter-note">Showing {visibleRows.length} of {rows.length} rows. Totals and the balance check always cover every row.</div>
					)}

					<div className="t__row t__row--lined ob-row ob-row--totals">
						<div className="t__row__item ob-col-num">Totals</div>
						<div className="t__row__item ob-col-name" />
						<div className="t__row__item ob-col-fs ob-row--totals-status">
							{isBalanced ? (
								<span className="ob-entry__status ob-entry__status--balanced"><Icon.CheckCircle size={14} /> In balance</span>
							) : (
								<span className="ob-entry__status ob-entry__status--error"><Icon.AlertCircle size={14} /> Out of balance</span>
							)}
						</div>
						<div className="t__row__item ob-col-amt">{UtilityService.formatCurrency(totalDebit)}</div>
						<div className="t__row__item ob-col-amt">{UtilityService.formatCurrency(totalCredit)}</div>
						<div className="t__row__item ob-col-x" />
					</div>
				</div>

				<div className="ob-entry__add-row">
					<button className="ob-entry__link" onClick={this.addRow}><Icon.Plus size={14} /> Add row</button>
				</div>

				<div className="ob-entry__footer">
					{onClose && <button type="button" className="tk-button tk-button--secondary" onClick={onClose}>Cancel</button>}
					<button
						type="button"
						className="tk-button tk-button--primary"
						onClick={this.onSave}
						disabled={!canSave || saving}
						title={!isBalanced ? "Balance the trial balance first" : (unmappedCount > 0 ? "Map every account first" : (!liveRows.length ? "Enter at least one account" : undefined))}
					>
						{saving ? "Saving…" : "Save opening balances"}
					</button>
				</div>
			</div>
		);
	}
}

export default OpeningBalanceEntry;
